import { NavLink } from "react-router-dom";

import { SectionHeading } from "@/components/sections/SectionHeading";

const overviewPoints = [
  {
    title: "Modular Governance",
    description:
      "Every module runs on the same policy layer, translating human rules into verifiable machine actions.",
  },
  {
    title: "Traceable Collaboration",
    description:
      "From creative ideation to compliance review, STRONOM records each decision as an immutable event.",
  },
  {
    title: "Universal Interoperability",
    description:
      "Connect studios, automation pipelines, and oversight tools with shared data contracts and connectors.",
  },
];

export const OverviewSection = () => {
  return (
    <section id="overview" className="relative py-24">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-16 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:items-center">
          <div className="space-y-10">
            <SectionHeading
              eyebrow="What is STRONOM?"
              title="A modular framework that governs intelligent systems"
              description="STRONOM defines how intelligence behaves, interacts, and proves its results. It turns creative and operational processes into structured, traceable, and reliable outputs."
            />

            <div className="space-y-6">
              <div className="grid gap-5 sm:grid-cols-2">
                {overviewPoints.map((point) => (
                  <div
                    key={point.title}
                    className="rounded-3xl border border-border/60 bg-card/70 p-6 shadow-[0_24px_80px_rgba(6,12,24,0.55)] backdrop-blur"
                  >
                    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-stronom-design/70">
                      {point.title}
                    </p>
                    <p className="mt-3 text-sm text-muted-foreground">{point.description}</p>
                  </div>
                ))}
              </div>

              <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
                Each module in the STRONOM Suite extends the same verification core. Whether you are shipping products, orchestrating automation, or governing digital services, the framework ensures every output is provable.
              </p>

              <NavLink
                to="/core"
                className="inline-flex items-center gap-2 text-sm font-semibold text-stronom-design transition-colors hover:text-stronom-design/80"
              >
                Learn more about the framework
                <span aria-hidden className="text-base">→</span>
              </NavLink>
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="absolute -inset-10 rounded-[3rem] bg-gradient-to-br from-white/8 via-white/3 to-transparent blur-3xl" />
            <div className="relative flex h-[420px] w-[420px] items-center justify-center">
              <div className="absolute inset-0 rounded-[3rem] border border-white/10 bg-black/40 shadow-[0_55px_160px_rgba(5,10,22,0.65)] backdrop-blur">
                <div className="absolute inset-[18%] rounded-[2.4rem] border border-white/8 bg-gradient-to-br from-white/6 via-transparent to-transparent" />
                <div className="absolute inset-[25%] rounded-[2rem] border border-stronom-design/35" />
                <div className="absolute inset-[37%] rounded-[1.6rem] border border-stronom-build/45" />
                <div className="absolute inset-[49%] rounded-[1.2rem] border border-stronom-compliance/45" />
                <div className="absolute inset-[25%] animate-[spin_28s_linear_infinite] rounded-[2rem] border border-stronom-design/20" />
                <div className="absolute inset-[37%] animate-[spin_24s_linear_infinite_reverse] rounded-[1.6rem] border border-stronom-build/20" />
                <div className="absolute inset-[49%] animate-[spin_20s_linear_infinite] rounded-[1.2rem] border border-stronom-gov/20" />
                <div className="absolute inset-[60%] flex items-center justify-center rounded-[0.8rem] bg-stronom-design/15">
                  <div className="size-14 rounded-xl bg-gradient-to-br from-stronom-design via-stronom-media to-stronom-gov opacity-90 shadow-[0_0_70px_hsl(var(--color-design)_/_0.4)]" />
                </div>
              </div>

              <div className="absolute -bottom-12 left-1/2 w-[320px] -translate-x-1/2 rounded-2xl border border-white/15 bg-card/80 px-6 py-5 text-xs text-muted-foreground backdrop-blur">
                <p className="font-semibold uppercase tracking-[0.35em] text-stronom-design/70">Unified Core</p>
                <p className="mt-2 text-sm text-foreground">
                  Shared policy, validation, and telemetry services align every module across the suite.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
